import '../IntegrationsTab.scss'
import React, { useMemo, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { SmartOfficeSettingsModal } from './SmartOfficeSettingsModal'
import { IntegrationServiceId } from '../../../../common/integrations'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { SmartOfficeIntegrationPrefillData } from '../../../Integration/Connectors/ModalIntegrationConnectors/SmartOffice/model'
import { SmartOfficeCredentialsModal } from '../../../Integration/Connectors/ModalIntegrationConnectors/SmartOffice/SmartOfficeCredentialsModal'

export type SmartOfficeIntegrationRowProps = {
  useIntegrationRedirectUri?: boolean
  openModal?: boolean
}
export const SmartOfficeIntegrationRow: React.FC<SmartOfficeIntegrationRowProps> = ({ useIntegrationRedirectUri, openModal }) => {
  const accountId = useAppSelector(selectAccountId)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasSmartOfficeIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.SMARTOFFICE))
  }, [userData])

  const [showSmartOfficeCredentialsModal, setShowSmartOfficeCredentialsModal] = useState<boolean>(false)
  const [integrationPrefill, setIntegrationPrefill] = useState<SmartOfficeIntegrationPrefillData | undefined>(undefined)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!openModal) return
    if (userData === undefined) return

    setShowSmartOfficeCredentialsModal(true)
  }, [openModal, userData])

  useEffect(() => {
    if (!searchParams) return

    const encodedSiteName = searchParams.get('siteName')
    const encodedOfficeName = searchParams.get('officeName')
    const encodedUserName = searchParams.get('userName')

    if (encodedSiteName && encodedOfficeName && encodedUserName) {
      setIntegrationPrefill({
        siteName: window.atob(encodedSiteName),
        officeName: window.atob(encodedOfficeName),
        username: window.atob(encodedUserName),
      })
    }
  }, [searchParams])

  const uninstallSmartOffice = () => {
    const user = userData
    const integration = user!!.integrations!!.find((i) => i.serviceId === IntegrationServiceId.SMARTOFFICE)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!!.id,
        integrationId: integration!!.id,
      },
    })
  }

  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }

  const handleSmartOfficeCredentialsModal = () => {
    setShowSmartOfficeCredentialsModal(false)
  }

  const handleConnect = () => {
    setShowSmartOfficeCredentialsModal(true)
  }

  const handleOnSave = () => {
    setShowSmartOfficeCredentialsModal(false)
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <SmartOfficeSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}
      <SmartOfficeCredentialsModal
        show={showSmartOfficeCredentialsModal}
        onSave={handleOnSave}
        onCancel={handleSmartOfficeCredentialsModal}
        prefillData={integrationPrefill}
      />
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.SMARTOFFICE} width={48} height={48} />
            </div>
            <div>
              <h6>SmartOffice</h6>
              <div>Connect your Zocks account to SmartOffice.</div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {hasSmartOfficeIntegration && (
              <div className='d-flex gap-3'>
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                  <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                    <i className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              </div>
            )}
            <div className='connect-button-container'>
              {hasSmartOfficeIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallSmartOffice} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={handleConnect}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
